import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #collectionImageSizes = new WeakMap();
  #collectionImageObserver = new MutationObserver(() => this.#syncCollectionImageSizes());

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    if (window.location.pathname.startsWith('/collections/') && this.refs.grid) {
      this.#collectionImageObserver.observe(this.refs.grid, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['sizes', 'product-grid-view'],
      });
    }
    this.#syncCollectionImageSizes();
  }

  disconnectedCallback() {
    this.#collectionImageObserver.disconnect();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    this.#syncCollectionImageSizes();
  }

  #syncCollectionImageSizes() {
    if (!window.location.pathname.startsWith('/collections/')) return;

    const { grid } = this.refs;
    if (!grid) return;

    const singleColumn = !mediaQueryLarge.matches && grid.getAttribute('product-grid-view') === 'mobile-single';
    for (const image of grid.querySelectorAll('img.product-media__image')) {
      if (!(image instanceof HTMLImageElement)) continue;

      const autoSizes = image.sizes.startsWith('auto, ');
      const defaultSizes = this.#collectionImageSizes.get(image) ?? (autoSizes ? image.sizes.slice(6) : image.sizes);
      if (!defaultSizes.startsWith('(max-width: 749px) calc(')) continue;

      this.#collectionImageSizes.set(image, defaultSizes);
      const mobileSizes = singleColumn ? '(max-width: 749px) 100vw, ' + defaultSizes : defaultSizes;
      const sizes = (autoSizes ? 'auto, ' : '') + mobileSizes;
      if (image.sizes !== sizes) image.sizes = sizes;
    }
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
